import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import DatasetView from './DatasetView';
import LineChart from './LineChart';
import { fmt, periodLabel } from './format';
import { SHAPES, VIEWBOX, MAP_CREDIT } from './nigeriaMap';
import SortTable from './SortTable';

/** Images live in one folder on thevoucha.com (hero.png, themes/{key}.jpg); missing ones fall back to an icon. */
const IMAGES = 'https://thevoucha.com/voucha-assets/datahub/';
const broken = new Set();
const img = (src, alt, cls) => broken.has(src) ? null : m("img", {
  className: cls,
  src: src,
  alt: alt,
  loading: "lazy",
  onerror: () => {
    broken.add(src);
    m.redraw();
  }
});
const TABS = [['overview', 'Overview', 'fas fa-chart-bar'], ['economy', 'Economy', 'fas fa-coins'], ['population', 'Population', 'fas fa-users'], ['government', 'Government', 'fas fa-landmark'], ['finance', 'Finance', 'far fa-credit-card'], ['/data-hub/guides', 'Location guides', 'fas fa-globe-africa'], ['/data-hub/elections', 'Elections', 'fas fa-vote-yea'], ['/data-hub/ecowas-trade', 'ECOWAS trade', 'fas fa-ship'], ['/data-hub/businesses', 'Businesses', 'fas fa-store'], ['more', 'More', 'fas fa-ellipsis-h']];
const THEMES = [['states', 'population', 'States & LGAs', 'Population, area, demographics', 'far fa-map'], ['government', 'government', 'Government', 'Governors, state rankings, traditional rulers', 'fas fa-landmark'], ['economy', 'economy', 'Economy & Finance', 'GDP, inflation, FAAC, exchange', 'fas fa-coins'], ['guides', '/data-hub/guides', 'Location guides', 'Canada, the UK and the US: history, communities, income', 'fas fa-globe-africa']];
const POPULAR = ['Population', 'GDP', 'Inflation', 'Exchange rate', 'FAAC', 'MPR', 'Unemployment'];

/** Which tab a dataset belongs to. */
function tabOf(d) {
  if (d.category === 'monetary') return 'finance';
  if (d.category === 'prices' || d.category === 'labour') return 'economy';
  if (d.category === 'fiscal') return d.level === 'state' ? 'government' : 'economy';
  if (d.category === 'population' || d.category === 'geography') return 'population';
  return 'more';
}
const themeOfTab = tab => ({
  economy: 'economy',
  finance: 'economy',
  population: 'states',
  government: 'government'
})[tab] || tab;
const ago = iso => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso)) / 1000;
  if (s < 3600) return Math.max(1, Math.round(s / 60)) + 'm ago';
  if (s < 86400) return Math.round(s / 3600) + 'h ago';
  if (s < 86400 * 7) return Math.round(s / 86400) + 'd ago';
  return Math.round(s / (86400 * 7)) + 'w ago';
};

/** Is this change good news, bad news or neither? (inflation rising is bad; reserves rising is good) */
const tone = k => !k.change || !k.better ? 'is-neutral' : k.change > 0 === (k.better === 'up') ? 'is-good' : 'is-bad';
const go = (e, url) => {
  if (e.ctrlKey || e.metaKey) return;
  e.preventDefault();
  m.route.set(url);
};

/** A state's name linking to its state page (when its slug is known) */
const stateLink = (name, slug) => slug && name ? m("a", {
  className: "DHStateLink",
  href: '/data-hub/states/' + slug,
  onclick: e => go(e, '/data-hub/states/' + slug)
}, name) : name;

/** /data-hub (home) and /data-hub/{key} (one dataset): full width, no sidebars. */
export default class DataHubPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'DataHubFull';
    this.home = null;
    this.datasets = null;
    const wanted = m.route.param('tab');
    this.tab = TABS.some(t => t[0] === wanted) ? wanted : 'overview';
    this.query = '';
    this.hover = null;
    this.trend = null;
    this.range = 'all';
    this.trendData = {};
    app.setTitle('Nigerian Data Hub');
    const api = app.forum.attribute('apiUrl') + '/datahub/';
    Promise.all([app.request({
      method: 'GET',
      url: api + 'home',
      errorHandler: () => {}
    }), app.request({
      method: 'GET',
      url: api + 'datasets',
      errorHandler: () => {}
    })]).then(([home, list]) => {
      this.home = home;
      this.datasets = list.datasets;
      if (home.trends.length) this.pickTrend(home.trends[0].key);
    }).catch(() => {
      this.home = {
        kpis: [],
        map: null,
        trends: [],
        updates: []
      };
      this.datasets = [];
    }).finally(() => m.redraw());
  }
  view() {
    const key = m.route.param('key');
    if (key) {
      return m("div", {
        className: "DataHubFull-page"
      }, m("div", {
        className: "container DataHubFull-dataset"
      }, m(DatasetView, {
        key: key,
        datasetKey: key
      })));
    }
    return m("div", {
      className: "DataHubFull-page"
    }, this.hero(), m("div", {
      className: "container"
    }, this.tabs(), !this.home ? m(LoadingIndicator, null) : this.query.trim() ? this.results() : this.tab === 'overview' ? this.overview() : this.tabContent(this.tab)));
  }

  // ---------------- hero + search ----------------
  hero() {
    return m("section", {
      className: "DHHero"
    }, m("div", {
      className: "container DHHero-inner"
    }, m("div", {
      className: "DHHero-text"
    }, m("div", {
      className: "DHHero-eyebrow"
    }, "Data hub"), m("h1", null, "Nigerian Data Hub"), m("p", null, "Key figures for Nigeria and the diaspora, with official sources."), m("form", {
      className: "DHHero-search",
      onsubmit: e => e.preventDefault()
    }, m("i", {
      className: "fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      type: "search",
      placeholder: "Search for data, states, indicators or topics\u2026",
      value: this.query,
      oninput: e => this.query = e.target.value,
      "aria-label": "Search the Data Hub"
    }), m("button", {
      type: "submit",
      className: "Button Button--primary"
    }, "Search")), m("div", {
      className: "DHHero-popular"
    }, m("span", null, "Popular searches:"), POPULAR.map(p => m("button", {
      className: 'DHChip' + (this.query.toLowerCase() === p.toLowerCase() ? ' is-active' : ''),
      onclick: () => this.query = p
    }, p)))), m("div", {
      className: "DHHero-art"
    }, img(IMAGES + 'hero.png', '', 'DHHero-img'))));
  }
  tabs() {
    return m("nav", {
      className: "DHTabs",
      "aria-label": "Data themes"
    }, TABS.map(([key, label, icon]) => m("button", {
      className: 'DHTab' + (this.tab === key && !this.query ? ' is-active' : ''),
      onclick: () => {
        if (key[0] === '/') return m.route.set(key);
        this.tab = key;
        this.query = '';
      }
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), m("span", null, label))));
  }

  // ---------------- search results / tab content ----------------
  matches(q) {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const alias = {
      'exchange rate': 'exchange',
      inflation: 'inflation',
      gdp: 'gdp',
      faac: 'faac',
      mpr: 'monetary policy'
    };
    const want = words.map(w => alias[w] || w);
    return this.datasets.filter(d => {
      const hay = [d.name, d.key, d.categoryLabel, d.notes, d.sourceName].join(' ').toLowerCase();
      return want.every(w => hay.includes(w)) || hay.includes(alias[q.toLowerCase()] || q.toLowerCase());
    });
  }
  results() {
    const list = this.matches(this.query.trim());
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("h2", null, "Results for \"", this.query.trim(), "\""), m("button", {
      className: "DHLink",
      onclick: () => this.query = ''
    }, "Clear search")), list.length ? this.cards(list) : m("p", {
      className: "DHMuted"
    }, "No datasets match yet. Try another word, or browse the themes below."));
  }
  cards(list, extra = null) {
    return m("div", {
      className: "DHCards"
    }, extra, list.map(d => m("a", {
      className: "DHCard",
      href: app.route('voucha-datahub.dataset', {
        key: d.key
      }),
      onclick: e => go(e, app.route('voucha-datahub.dataset', {
        key: d.key
      }))
    }, m("small", null, d.categoryLabel), m("strong", null, d.name), d.headline ? m("span", {
      className: "DHCard-big"
    }, fmt(d.headline.value, d.unit)) : null, m("span", {
      className: "DHMuted"
    }, d.headline ? d.headline.label : ''))));
  }

  /** Government: a row of cards (governors and state finances), then state rankings and traditional rulers. */
  government(list) {
    if (!this.gov) {
      this.gov = {
        states: null,
        ranking: null,
        rulers: null
      };
      const api = app.forum.attribute('apiUrl');
      app.request({
        method: 'GET',
        url: api + '/voucha-community/governors',
        errorHandler: () => {}
      }).then(r => {
        this.gov.states = r.governors;
        this.gov.ranking = r.ranking;
      }).catch(() => this.gov.states = []).finally(() => m.redraw());
      app.request({
        method: 'GET',
        url: api + '/datahub/rulers',
        errorHandler: () => {}
      }).then(r => this.gov.rulers = r.rulers).catch(() => this.gov.rulers = []).finally(() => m.redraw());
    }
    const g = this.gov;
    const n = (v, d = 0) => v === null || v === undefined ? null : Number(v).toLocaleString('en-US', {
      maximumFractionDigits: d
    });
    const val = o => o && typeof o === 'object' ? o.value : o;
    const stateCols = [{
      key: 'rank',
      label: 'Rank',
      sort: r => r.rank,
      show: r => r.rank ? '#' + r.rank : null,
      csv: r => r.rank
    }, {
      key: 'state',
      label: 'State',
      strong: true,
      sort: r => r.state,
      show: r => stateLink(r.state, r.slug),
      csv: r => r.state
    }, {
      key: 'zone',
      label: 'Region',
      sort: r => r.zone,
      show: r => r.zone
    }, {
      key: 'population',
      label: 'Population',
      desc: true,
      sort: r => r.population,
      show: r => n(r.population)
    }, {
      key: 'score',
      label: 'Score (of 100)',
      desc: true,
      sort: r => r.score,
      show: r => r.score === null || r.score === undefined ? null : Math.round(r.score)
    }, {
      key: 'igr',
      label: 'IGR (₦ bn)',
      desc: true,
      sort: r => val(r.igr),
      show: r => n(val(r.igr))
    }, {
      key: 'gdp',
      label: 'GDP (₦ trn)',
      desc: true,
      sort: r => val(r.gdp),
      show: r => n(val(r.gdp), 2)
    }, {
      key: 'faac',
      label: 'FAAC (₦ bn)',
      desc: true,
      sort: r => r.faacRecent ? r.faacRecent.value : null,
      show: r => r.faacRecent ? n(r.faacRecent.value) + (r.faacRecent.months < 12 ? ' (' + r.faacRecent.months + ' mo)' : '') : null,
      csv: r => r.faacRecent ? r.faacRecent.value : null
    }, {
      key: 'ratio',
      label: 'GDP ÷ FAAC',
      desc: true,
      sort: r => r.ratio,
      show: r => r.ratio ? r.ratio.toFixed(1) + '×' : null
    }, {
      key: 'debtDom',
      label: 'Debt, domestic (₦ bn)',
      desc: true,
      sort: r => val(r.debtDomestic),
      show: r => n(val(r.debtDomestic))
    }, {
      key: 'debtExt',
      label: 'Debt, external (US$ m)',
      desc: true,
      sort: r => val(r.debtExternal),
      show: r => n(val(r.debtExternal))
    }, {
      key: 'rulers',
      label: 'Traditional rulers',
      desc: true,
      sort: r => r.rulers,
      show: r => r.rulers
    }, {
      key: 'lgas',
      label: 'LGAs',
      desc: true,
      sort: r => r.lgaCount,
      show: r => r.lgaCount
    }, {
      key: 'governor',
      label: 'Governor',
      sort: r => r.name,
      show: r => r.name
    }];
    const rulerCols = [{
      key: 'rank',
      label: 'Rank',
      sort: r => r.rank,
      show: r => r.rank ? '#' + r.rank : null,
      csv: r => r.rank
    }, {
      key: 'kingdom',
      label: 'Kingdom / emirate',
      strong: true,
      sort: r => r.kingdom,
      show: r => r.kingdom
    }, {
      key: 'title',
      label: 'Title',
      sort: r => r.title,
      show: r => r.title
    }, {
      key: 'name',
      label: 'Ruler',
      sort: r => r.name,
      show: r => r.name
    }, {
      key: 'state',
      label: 'State',
      sort: r => r.state,
      show: r => stateLink(r.state, r.slug),
      csv: r => r.state
    }, {
      key: 'grade',
      label: 'Class',
      sort: r => r.grade,
      show: r => r.grade
    }, {
      key: 'since',
      label: 'Ruler since',
      desc: true,
      sort: r => r.since,
      show: r => r.since ? new Date(r.since).getFullYear() : null
    }, {
      key: 'founded',
      label: 'Founded',
      sort: r => r.years == null ? null : -r.years,
      show: r => r.founded
    }, {
      key: 'years',
      label: 'Years',
      desc: true,
      sort: r => r.years,
      show: r => r.years == null ? null : (/c\./.test(r.founded || '') ? 'c. ' : '') + r.years.toLocaleString('en-US'),
      csv: r => r.years
    }, {
      key: 'source',
      label: 'Source',
      sort: r => r.source,
      show: r => r.source ? m("a", {
        href: r.source,
        target: "_blank",
        rel: "noopener"
      }, "Link ", m("i", {
        className: "fas fa-external-link-alt"
      })) : null,
      csv: r => r.source
    }];
    const governors = m("a", {
      className: "DHCard",
      href: "/community/governors",
      onclick: e => go(e, '/community/governors')
    }, m("small", null, "Leadership"), m("strong", null, "Governors"), m("span", {
      className: "DHCard-big"
    }, "36 + 1"), m("span", {
      className: "DHMuted"
    }, "36 governors + FCT minister, with photos and details"));
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("h2", null, "Government")), this.cards(list, governors), m("div", {
      className: "DHSection-head DHSub"
    }, m("div", null, m("h3", null, "State rankings"), m("p", {
      className: "DHMuted"
    }, "The 36 states, ranked by ", g.ranking && g.ranking.measure || 'the interim measure', ". The FCT is listed but not ranked: it has no governor and is funded differently. Click a column to sort."))), g.states === null ? m(LoadingIndicator, null) : g.states.length ? m(SortTable, {
      cols: stateCols.filter(c => ['rank', 'state', 'zone', 'governor'].includes(c.key) || g.states.some(r => {
        const v = c.show(r);
        return v !== null && v !== undefined && v !== '';
      })),
      rows: g.states,
      filename: "nigeria-state-rankings.csv",
      sort: "rank",
      limit: 10,
      search: "Search states, regions or governors"
    }) : m("p", {
      className: "DHMuted"
    }, "State figures aren't available."), m("div", {
      className: "DHSection-head DHSub"
    }, m("div", null, m("h3", null, "Traditional rulers & kingdoms"), m("p", {
      className: "DHMuted"
    }, "Kingdoms, emirates and chiefdoms: their titles, current rulers and how long each has existed. \"c.\" means approximate."))), g.rulers === null ? m(LoadingIndicator, null) : g.rulers.length ? m(SortTable, {
      cols: rulerCols,
      rows: g.rulers,
      filename: "nigeria-traditional-rulers.csv",
      sort: "rank",
      limit: 10,
      search: "Search kingdoms, titles, rulers or states"
    }) : m("div", {
      className: "DHEmpty"
    }, m("i", {
      className: "fas fa-crown"
    }), m("p", null, m("strong", null, "No traditional rulers recorded yet."), " They'll appear here once they're added to the Data Hub.")));
  }
  tabContent(tab) {
    if (tab === 'government') return this.government(this.datasets.filter(d => tabOf(d) === 'government'));
    const label = (TABS.find(t => t[0] === tab) || [])[1];
    const list = tab === 'more' ? this.datasets : this.datasets.filter(d => tabOf(d) === tab);
    const governors = tab === 'government' ? m("a", {
      className: "DHCard",
      href: "/community/governors",
      onclick: e => go(e, '/community/governors')
    }, m("small", null, "Leadership"), m("strong", null, "Governors"), m("span", {
      className: "DHCard-big"
    }, "36 + 1"), m("span", {
      className: "DHMuted"
    }, "36 governors + FCT minister, with rankings")) : null;
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("h2", null, tab === 'more' ? 'All datasets' : label)), list.length || governors ? this.cards(list, governors) : m("div", {
      className: "DHEmpty"
    }, m("i", {
      className: (TABS.find(t => t[0] === tab) || [])[2]
    }), m("p", null, m("strong", null, label, " data is coming."), " These figures will appear here as they're added to the Data Hub, each with its official source.")));
  }

  // ---------------- overview ----------------
  overview() {
    const h = this.home;
    return [m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "National Overview"), m("p", {
      className: "DHMuted"
    }, "Key indicators for Nigeria. Latest available data from official sources.")), h.lastUpdated ? m("span", {
      className: "DHMuted"
    }, "Last updated: ", new Date(h.lastUpdated).toLocaleDateString('en-GB', {
      dateStyle: 'medium'
    })) : null), m("div", {
      className: "DHKpis"
    }, h.kpis.map(k => this.kpi(k)))), m("section", {
      className: "DHSection DHPair"
    }, this.mapCard(h.map), this.trendsCard()), this.themes(), this.updates(h.updates)];
  }
  kpi(k) {
    const up = k.change > 0;
    const change = k.change === null ? null : (up ? '+' : '') + k.change + (k.changeUnit === 'pts' ? ' pts' : '%');
    return m("a", {
      className: "DHKpi",
      href: app.route('voucha-datahub.dataset', {
        key: k.key
      }),
      onclick: e => go(e, app.route('voucha-datahub.dataset', {
        key: k.key
      }))
    }, m("div", {
      className: "DHKpi-head"
    }, m("i", {
      className: k.icon,
      "aria-hidden": "true"
    }), " ", k.label), m("div", {
      className: "DHKpi-body"
    }, m("div", null, m("div", {
      className: "DHKpi-value"
    }, fmt(k.value, k.unit, true)), change ? m("div", {
      className: 'DHKpi-change ' + tone(k)
    }, m("i", {
      className: 'fas fa-arrow-' + (up ? 'up' : 'down')
    }), " ", change) : m("div", {
      className: "DHKpi-change"
    }, "\xA0")), this.spark(k.spark)), m("div", {
      className: "DHMuted DHKpi-source"
    }, k.sourceName || k.source || 'Data Hub', " \xB7 ", k.older ? 'latest available: ' : '', periodLabel(k.period), k.retrievedAt ? ' · retrieved ' + new Date(k.retrievedAt).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    }) : ''));
  }
  spark(values) {
    if (!values || values.length < 2) return m("span", {
      className: "DHSpark"
    });
    const W = 110,
      H = 44;
    const min = Math.min(...values),
      max = Math.max(...values),
      span = max - min || 1;
    const pts = values.map((v, i) => `${i / (values.length - 1) * W},${H - 4 - (v - min) / span * (H - 8)}`);
    return m("svg", {
      className: "DHSpark",
      viewBox: `0 0 ${W} ${H}`,
      "aria-hidden": "true"
    }, m("polygon", {
      points: `0,${H} ${pts.join(' ')} ${W},${H}`,
      className: "DHSpark-area"
    }), m("polyline", {
      points: pts.join(' '),
      className: "DHSpark-line"
    }));
  }
  mapCard(map) {
    if (!map) return m("div", {
      className: "DHPanel"
    });
    const values = Object.values(map.values).map(v => v.value).sort((a, b) => a - b);
    // five shades from quintiles, rounded to the nearest 0.5M for a readable legend
    const q = [0.2, 0.4, 0.6, 0.8].map(p => Math.round(values[Math.floor(p * (values.length - 1))] / 5e5) * 5e5);
    const shade = v => v === undefined ? 'DHMap-none' : 'DHMap-' + (q.filter(t => v >= t).length + 1);
    const M = v => (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    const legend = [['5', '> ' + M(q[3])], ['4', M(q[2]) + ' – ' + M(q[3])], ['3', M(q[1]) + ' – ' + M(q[2])], ['2', M(q[0]) + ' – ' + M(q[1])], ['1', '< ' + M(q[0])]];
    const shown = this.hover || this.pinned; // a click/tap keeps a state in the side panel, so its link can be clicked
    const hovered = shown && map.values[shown];
    return m("div", {
      className: "DHPanel DHMapCard"
    }, m("h3", null, "Nigeria by State"), m("p", {
      className: "DHMuted"
    }, map.name, map.period ? ' (' + map.period + ')' : ''), m("div", {
      className: "DHMap"
    }, m("svg", {
      viewBox: VIEWBOX,
      role: "img",
      "aria-label": 'Map of Nigeria: ' + map.name + ' by state'
    }, Object.entries(SHAPES).map(([slug, d]) => m("path", {
      d: d,
      className: shade(map.values[slug] && map.values[slug].value) + (shown === slug ? ' is-hover' : ''),
      onmouseenter: () => this.hover = slug,
      onmouseleave: () => this.hover = null,
      onclick: () => this.pinned = this.pinned === slug ? null : slug
    }))), m("div", {
      className: "DHMap-side"
    }, m("ul", {
      className: "DHMap-legend"
    }, legend.map(([n, label]) => m("li", null, m("span", {
      className: 'DHMap-' + n
    }), " ", label))), m("div", {
      className: "DHMap-hover"
    }, hovered ? [m("strong", null, hovered.name), m("span", null, Math.round(hovered.value).toLocaleString('en-US')), m("a", {
      className: "DHLink",
      href: '/data-hub/states/' + shown,
      onclick: e => go(e, '/data-hub/states/' + shown)
    }, "State profile ", m("i", {
      className: "fas fa-arrow-right"
    }))] : m("span", {
      className: "DHMuted"
    }, "Hover or tap a state")))), m("div", {
      className: "DHPanel-foot"
    }, m("a", {
      className: "Button",
      href: app.route('voucha-datahub.dataset', {
        key: map.key
      }),
      onclick: e => go(e, app.route('voucha-datahub.dataset', {
        key: map.key
      }))
    }, "View all states ", m("i", {
      className: "fas fa-arrow-right"
    })), m("a", {
      className: "Button DHLinkButton",
      href: "/data-hub/states",
      onclick: e => go(e, '/data-hub/states')
    }, m("i", {
      className: "far fa-map"
    }), " State profiles"), m("small", {
      className: "DHMuted"
    }, MAP_CREDIT)));
  }
  pickTrend(key) {
    this.trend = key;
    if (this.trendData[key]) return;
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/datahub/datasets/' + encodeURIComponent(key),
      errorHandler: () => {}
    }).then(d => this.trendData[key] = d).catch(() => this.trendData[key] = {
      series: []
    }).finally(() => m.redraw());
  }
  trendsCard() {
    const opts = this.home.trends;
    const d = this.trendData[this.trend];
    let points = [];
    if (d && d.series) {
      const ng = d.series.find(s => s.id === 566) || d.series[0];
      points = ng ? ng.points : [];
    }
    return m("div", {
      className: "DHPanel"
    }, m("div", {
      className: "DHPanel-head"
    }, m("div", null, m("h3", null, "Key Trends"), m("p", {
      className: "DHMuted"
    }, "Since January ", d && d.fromYear ? d.fromYear : new Date().getFullYear() - 1)), opts.length ? m("select", {
      className: "FormControl DHTrendSelect",
      value: this.trend,
      onchange: e => this.pickTrend(e.target.value),
      "aria-label": "Choose a dataset"
    }, opts.map(o => m("option", {
      value: o.key
    }, o.name))) : null), !opts.length ? m("p", {
      className: "DHMuted"
    }, "Trends appear here as history is added to the Data Hub.") : !d ? m(LoadingIndicator, null) : points.length ? m(LineChart, {
      series: [{
        id: 566,
        name: 'Nigeria',
        points
      }],
      unit: d.unit,
      area: true
    }) : m("p", {
      className: "DHMuted"
    }, "No figures in this range yet."));
  }
  themes() {
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "Explore Data Themes"), m("p", {
      className: "DHMuted"
    }, "Browse key data categories with official sources, charts and analysis.")), m("button", {
      className: "Button DHLinkButton",
      onclick: () => {
        this.tab = 'more';
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, "View all categories ", m("i", {
      className: "fas fa-arrow-right"
    }))), m("div", {
      className: "DHThemes"
    }, THEMES.map(([key, tab, label, sub, icon]) => m("button", {
      className: "DHTheme",
      onclick: () => {
        if (tab[0] === '/') return m.route.set(tab);
        this.tab = tab;
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, m("div", {
      className: "DHTheme-img"
    }, img(IMAGES + 'themes/' + key + '.jpg', '', ''), m("i", {
      className: icon,
      "aria-hidden": "true"
    })), m("div", {
      className: "DHTheme-body"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), m("span", null, m("strong", null, label), m("small", null, sub)), m("i", {
      className: "fas fa-chevron-right DHTheme-go",
      "aria-hidden": "true"
    }))))));
  }
  updates(list) {
    if (!list || !list.length) return null;
    const label = c => ({
      monetary: 'Finance',
      prices: 'Economy',
      fiscal: 'Economy',
      labour: 'Economy',
      population: 'Population',
      geography: 'Population'
    })[c] || 'Data';
    return m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("h2", null, "Latest Updates"), m("button", {
      className: "DHLink",
      onclick: () => {
        this.tab = 'more';
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, "View all data ", m("i", {
      className: "fas fa-arrow-right"
    }))), m("div", {
      className: "DHUpdates"
    }, list.map(u => {
      const theme = themeOfTab(tabOf(u));
      const title = u.value !== null ? u.name + ': ' + fmt(u.value, u.unit) + (u.period ? ' (' + periodLabel(u.period) + ')' : '') : u.name + (u.places ? ' updated for ' + u.places + (u.level === 'lga' ? ' LGAs' : ' states') : ' updated');
      return m("a", {
        className: "DHUpdate",
        href: app.route('voucha-datahub.dataset', {
          key: u.key
        }),
        onclick: e => go(e, app.route('voucha-datahub.dataset', {
          key: u.key
        }))
      }, m("div", {
        className: "DHUpdate-img"
      }, img(IMAGES + 'themes/' + theme + '.jpg', '', '')), m("div", {
        className: "DHUpdate-body"
      }, m("div", {
        className: "DHUpdate-meta"
      }, m("span", {
        className: 'DHPill is-' + label(u.category).toLowerCase()
      }, label(u.category)), m("span", {
        className: "DHMuted"
      }, ago(u.at))), m("strong", null, title), m("small", {
        className: "DHMuted"
      }, u.source || 'Data Hub')));
    })));
  }
}